import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: 'light',
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({ children, defaultTheme = 'light', storageKey = 'customer-portal-theme', ...props }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => (localStorage.getItem(storageKey) as Theme) || defaultTheme);

  // Check if we're in preview mode
  const isPreviewMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('preview') === 'true';

  useEffect(() => {
    const root = window.document.documentElement;

    // Remove both light and dark classes
    root.classList.remove('light', 'dark');

    // Add the current theme class
    // This works with data-theme attribute: :root[data-theme='...'].dark
    root.classList.add(theme);
  }, [theme]);

  // Listen for theme updates from parent (Hub) in preview mode
  useEffect(() => {
    if (!isPreviewMode) return;

    const handleMessage = (event: MessageEvent) => {
      // Validate origin - allow localhost on any port for development
      const isLocalhost =
        event.origin.startsWith('http://localhost:') ||
        event.origin.startsWith('https://localhost') ||
        event.origin.startsWith('http://127.0.0.1:') ||
        event.origin.startsWith('https://127.0.0.1:') ||
        event.origin.startsWith('https://hub.localhost');

      if (!isLocalhost) {
        return;
      }

      // Handle theme update messages
      if (event.data?.type === 'THEME_UPDATE' && event.data?.payload?.theme) {
        const newTheme = event.data.payload.theme as 'light' | 'dark';
        // Update theme state (this will trigger the useEffect above to apply it)
        // Skip localStorage save in preview mode since theme is controlled by Hub
        setThemeState(newTheme);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isPreviewMode]);

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      if (!isPreviewMode) {
        localStorage.setItem(storageKey, newTheme);
      }
      setThemeState(newTheme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
